import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { AnchorRect } from '../features/day/dayUiStore';
import { useGhostClickGuard } from './ghostClick';
import styles from './Popover.module.css';

interface PopoverProps {
  readonly anchor: AnchorRect;
  readonly label: string;
  readonly onClose: () => void;
  /** Esc, when it should differ from a press outside (the picker: Esc cancels, outside saves). Defaults to `onClose`. */
  readonly onEscape?: () => void;
  readonly children: ReactNode;
}

const GAP_PX = 8;
const EDGE_PX = 8;

/**
 * A floating panel beside an anchor rectangle: right of it when there is room, else left, and
 * clamped to the viewport. Esc and a press outside close it; the press is consumed so it does
 * not also act on whatever lies beneath (the next click starts a fresh interaction). Focus moves
 * into the popover on open and returns to the element that had it (the opener) on close.
 */
export function Popover({ anchor, label, onClose, onEscape, children }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const ghostGuard = useGhostClickGuard();
  // Read while rendering, before the layout effect below moves focus into the popover.
  const [opener] = useState<Element | null>(() => document.activeElement);

  useEffect(
    () => () => {
      const focus = document.activeElement;
      const focusIsLost = focus === null || focus === document.body || ref.current?.contains(focus) === true;
      // Only when focus is still in the popover (or nowhere): never take it from where the user moved it.
      if (focusIsLost && opener instanceof HTMLElement && opener.isConnected && opener !== document.body) {
        opener.focus({ preventScroll: true });
      }
    },
    [opener],
  );

  const place = useCallback((): void => {
    const element = ref.current;
    if (element === null) return;
    const { width, height } = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const fitsRight = anchor.right + GAP_PX + width <= viewportWidth - EDGE_PX;
    const left = fitsRight ? anchor.right + GAP_PX : Math.max(EDGE_PX, anchor.left - GAP_PX - width);
    const top = Math.min(Math.max(EDGE_PX, anchor.top), Math.max(EDGE_PX, viewportHeight - height - EDGE_PX));
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    element.style.visibility = 'visible';
  }, [anchor]);

  useLayoutEffect(() => {
    place();
    ref.current?.focus({ preventScroll: true });
  }, [place]);

  // Content that changes size after placement (the picker's project dropdown opening or closing)
  // re-runs the placement in both directions: it moves up to stay inside the viewport when it
  // grows and returns to the anchor when it shrinks.
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(place);
    observer.observe(element);
    return () => observer.disconnect();
  }, [place]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent): void {
      if (ref.current !== null && event.target instanceof Node && ref.current.contains(event.target)) return;
      // The popover is non-modal towards the timeline (ui-redesign §3.2.1): a press inside a
      // lane is left for the lane to interpret — empty space dismisses (consumed by the lane),
      // a block press retargets the popover. Everything else closes and consumes here.
      if (event.target instanceof Element && event.target.closest('[data-testid="lane"]') !== null) return;
      event.stopPropagation();
      onClose();
    }
    function onKeyDown(event: KeyboardEvent): void {
      // An Esc already consumed upstream (the lane cancelling a drag, §3.2.1) never also
      // closes the popover: one key press, one step of the priority order.
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      (onEscape ?? onClose)();
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, onEscape]);

  return createPortal(
    <div
      ref={ref}
      className={styles.popover}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      style={{ visibility: 'hidden' }}
      data-testid="popover"
      // The gesture that opened the popover ends in a synthesized click; when the popover now
      // sits under the pointer that ghost click must not activate a control (see ghostClick.ts).
      onPointerDownCapture={ghostGuard.onPointerDownCapture}
      onClickCapture={ghostGuard.onClickCapture}
    >
      {children}
    </div>,
    document.body,
  );
}
